import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import { useLang } from "../LanguageContext";
import { LogOut, Upload, Save, Trash2, Mail, MailOpen, Loader2, Check, Plus } from "lucide-react";

const setByPath = (obj, path, value) => {
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  const clone = Array.isArray(obj) ? [...obj] : { ...obj };
  clone[head] = setByPath(obj ? obj[head] : undefined, rest, value);
  return clone;
};

const getByPath = (obj, path) => path.reduce((o, k) => (o == null ? o : o[k]), obj);

const labelize = (k) => String(k).replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());

// Friendly names + which IMAGES keys actually drive site sections
const SECTION_IMAGE_KEYS = ["hero", "workshop", "gripper", "factory", "defense"];
const IMAGE_LABELS = {
  hero: "Startsida – Hero-bakgrund",
  workshop: "Sektion: Intro (Om oss) – bild",
  gripper: "Sektion: Automation – bakgrund",
  factory: "Sektion: Företaget – bild",
  defense: "Sektion: Försvar – bakgrund",
};

const ImageField = ({ label, url, onChange, onUpload, uploading }) => (
  <div className="border border-border rounded overflow-hidden bg-white">
    <div className="aspect-video bg-paper-warm overflow-hidden">
      {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : null}
    </div>
    <div className="p-3">
      <p className="text-xs uppercase tracking-[0.12em] text-ink/60 mb-2">{label}</p>
      <input value={url} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-border focus:border-gold outline-none rounded px-2 py-1.5 text-xs text-ink mb-2" placeholder="Bild-URL" />
      <label className="flex items-center justify-center gap-2 cursor-pointer bg-ink hover:bg-ink-soft text-white text-xs py-2 rounded transition-colors">
        {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
        Ladda upp
        <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])} />
      </label>
    </div>
  </div>
);

const TABS = [
  { id: "texts", label: "Texter" },
  { id: "products", label: "Produkter" },
  { id: "images", label: "Bilder" },
  { id: "partners", label: "Kunder" },
  { id: "policies", label: "Policyer" },
  { id: "enquiries", label: "Förfrågningar" },
];

const Admin = () => {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const { refresh } = useLang();
  const [tab, setTab] = useState("texts");
  const [content, setContent] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [editLang, setEditLang] = useState("sv");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/content").then(({ data }) => setContent(data)).catch(() => {});
    api.get("/enquiries").then(({ data }) => setEnquiries(data)).catch(() => {});
  }, [user]);

  const onFieldChange = (path, value) => setContent((c) => setByPath(c, path, value));

  // Recursive RENDER FUNCTION (not a self-referencing component — avoids babel plugin recursion)
  const renderField = (value, path, keyName) => {
    const key = path.join(".");
    if (typeof value === "string") {
      const long = value.length > 60 || value.includes("\n");
      return (
        <label key={key} className="block mb-4">
          <span className="text-xs uppercase tracking-[0.12em] text-ink/50">{labelize(keyName)}</span>
          {long ? (
            <textarea rows={3} value={value} onChange={(e) => onFieldChange(path, e.target.value)}
              className="mt-1.5 w-full border border-border focus:border-gold outline-none rounded px-3 py-2 text-ink text-sm bg-white resize-y" />
          ) : (
            <input value={value} onChange={(e) => onFieldChange(path, e.target.value)}
              className="mt-1.5 w-full border border-border focus:border-gold outline-none rounded px-3 py-2 text-ink text-sm bg-white" />
          )}
        </label>
      );
    }
    if (Array.isArray(value)) {
      const isStrings = value.every((x) => typeof x === "string");
      const template = isStrings ? "" : JSON.parse(JSON.stringify(value[value.length - 1] || {}));
      return (
        <div key={key} className="mb-4 pl-4 border-l-2 border-paper-warm">
          {keyName ? <span className="text-xs uppercase tracking-[0.12em] text-gold">{labelize(keyName)}</span> : null}
          <div className="mt-2 space-y-2">
            {value.map((item, i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="flex-1">{renderField(item, [...path, i], `#${i + 1}`)}</div>
                <button type="button" onClick={() => removeArrayItem(path, i)} title="Ta bort"
                  className="mt-1 p-1.5 border border-border rounded hover:border-red-400 text-ink/40 hover:text-red-500 transition-colors shrink-0">
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => addArrayItem(path, template)}
            className="mt-3 flex items-center gap-1.5 text-xs border border-border hover:border-gold text-ink/70 rounded px-3 py-1.5 transition-colors">
            <Plus size={13} /> Lägg till
          </button>
        </div>
      );
    }
    if (value && typeof value === "object") {
      return (
        <div key={key} className="mb-4 pl-4 border-l-2 border-paper-warm">
          {keyName ? <span className="text-xs uppercase tracking-[0.12em] text-gold">{labelize(keyName)}</span> : null}
          <div className="mt-2">
            {Object.entries(value).map(([k, v]) => renderField(v, [...path, k], k))}
          </div>
        </div>
      );
    }
    return null;
  };

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await api.put("/content", {
        CONTENT: content.CONTENT, COLLECTIONS: content.COLLECTIONS,
        IMAGES: content.IMAGES, PARTNERS: content.PARTNERS,
      });
      await refresh();
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      alert(e?.response?.data?.detail || "Kunde inte spara.");
    } finally { setSaving(false); }
  };

  const uploadImage = async (file, applyUrl, key) => {
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      applyUrl(data.path);
    } catch (e) {
      alert(e?.response?.data?.detail || "Uppladdning misslyckades.");
    } finally { setUploadingKey(null); }
  };

  const addGalleryItem = (i) => setContent((c) => {
    const item = { image: "", sv: { title: "", desc: "" }, en: { title: "", desc: "" } };
    const gallery = [...(c.COLLECTIONS[i].gallery || []), item];
    return setByPath(c, ["COLLECTIONS", i, "gallery"], gallery);
  });
  const removeGalleryItem = (i, j) => setContent((c) => {
    const gallery = (c.COLLECTIONS[i].gallery || []).filter((_, idx) => idx !== j);
    return setByPath(c, ["COLLECTIONS", i, "gallery"], gallery);
  });

  const updatePartner = (idx, value) => onFieldChange(["PARTNERS", idx], value);
  const addArrayItem = (path, template) => setContent((c) => setByPath(c, path, [...(getByPath(c, path) || []), template]));
  const removeArrayItem = (path, index) => setContent((c) => setByPath(c, path, (getByPath(c, path) || []).filter((_, i) => i !== index)));
  const addPartner = () => setContent((c) => setByPath(c, ["PARTNERS"], [...(c.PARTNERS || []), ""]));
  const removePartner = (idx) => setContent((c) => setByPath(c, ["PARTNERS"], (c.PARTNERS || []).filter((_, i) => i !== idx)));


  const markRead = async (eid, read) => {
    await api.patch(`/enquiries/${eid}`, { read });
    setEnquiries((list) => list.map((e) => (e.id === eid ? { ...e, read } : e)));
  };
  const delEnquiry = async (eid) => {
    if (!window.confirm("Radera denna förfrågan?")) return;
    await api.delete(`/enquiries/${eid}`);
    setEnquiries((list) => list.filter((e) => e.id !== eid));
  };

  if (loading || !content) {
    return <div className="min-h-screen bg-paper flex items-center justify-center"><Loader2 className="animate-spin text-gold" /></div>;
  }

  const unread = enquiries.filter((e) => !e.read).length;

  return (
    <div className="min-h-screen bg-paper">
      <header className="sticky top-0 z-40 bg-ink text-white">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/horns-logo-dark.webp" alt="Horns" className="h-6 w-auto" />
            <span className="overline text-gold">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white/60 text-sm hidden sm:inline">{user?.email}</span>
            <button onClick={save} disabled={saving}
              className="flex items-center gap-2 bg-gold hover:bg-gold-light text-white px-4 py-2 text-xs uppercase tracking-[0.12em] rounded transition-colors disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin" /> : saved ? <Check size={14} /> : <Save size={14} />}
              {saved ? "Sparat" : "Spara"}
            </button>
            <button onClick={() => { logout(); navigate("/login"); }} className="text-white/60 hover:text-white transition-colors"><LogOut size={18} /></button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 flex gap-6 border-t border-white/10">
          {TABS.map((tb) => (
            <button key={tb.id} onClick={() => setTab(tb.id)}
              className={"py-3 text-sm tracking-[0.1em] uppercase border-b-2 transition-colors " + (tab === tb.id ? "border-gold text-white" : "border-transparent text-white/50 hover:text-white")}>
              {tb.label}{tb.id === "enquiries" && unread ? <span className="ml-2 bg-gold text-ink rounded-full px-1.5 text-[10px]">{unread}</span> : null}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {tab === "texts" && (
          <div>
            <div className="flex items-center gap-2 mb-8">
              {["sv", "en"].map((l) => (
                <button key={l} onClick={() => setEditLang(l)}
                  className={"px-4 py-1.5 text-xs uppercase tracking-[0.15em] rounded border transition-colors " + (editLang === l ? "bg-ink text-white border-ink" : "border-border text-ink/60 hover:border-gold")}>
                  {l === "sv" ? "Svenska" : "English"}
                </button>
              ))}
              <p className="text-ink/40 text-xs ml-3">Redigerar {editLang === "sv" ? "svenska" : "engelska"} texter</p>
            </div>
            <div className="grid md:grid-cols-2 gap-x-10">
              {Object.entries(content.CONTENT[editLang]).filter(([section]) => section !== "policies").map(([section, val]) => (
                <div key={section} className="mb-6 bg-white/60 rounded p-4 border border-border">
                  <h3 className="font-serif text-xl text-ink mb-3">{labelize(section)}</h3>
                  {renderField(val, ["CONTENT", editLang, section], "")}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "products" && (
          <div className="space-y-8">
            {content.COLLECTIONS.map((col, i) => (
              <div key={col.id} className="grid md:grid-cols-[220px_1fr] gap-6 bg-white/60 border border-border rounded p-5">
                <ImageField label={`Bild — ${col.id}`} url={col.image}
                  onChange={(url) => onFieldChange(["COLLECTIONS", i, "image"], url)}
                  onUpload={(f) => uploadImage(f, (url) => onFieldChange(["COLLECTIONS", i, "image"], url), col.id)}
                  uploading={uploadingKey === col.id} />
                <div>
                  <div className="grid sm:grid-cols-2 gap-x-6">
                    {["sv", "en"].map((l) => (
                      <div key={l}>
                        <p className="text-[11px] uppercase tracking-[0.14em] text-ink/40 mb-2">{l === "sv" ? "Svenska" : "English"}</p>
                        {renderField(col[l], ["COLLECTIONS", i, l], "")}
                      </div>
                    ))}
                  </div>

                  {/* Gallery editor */}
                  <div className="mt-6 border-t border-border pt-5">
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-xs uppercase tracking-[0.14em] text-gold">Bildgalleri (med teknisk beskrivning)</p>
                      <button onClick={() => addGalleryItem(i)}
                        className="flex items-center gap-1.5 text-xs border border-border hover:border-gold text-ink/70 rounded px-3 py-1.5 transition-colors">
                        <Plus size={13} /> Lägg till bild
                      </button>
                    </div>
                    <div className="space-y-4">
                      {(col.gallery || []).map((g, j) => (
                        <div key={j} className="grid md:grid-cols-[180px_1fr_auto] gap-4 items-start bg-paper/60 border border-border rounded p-3">
                          <ImageField label={`Bild ${j + 1}`} url={g.image}
                            onChange={(u) => onFieldChange(["COLLECTIONS", i, "gallery", j, "image"], u)}
                            onUpload={(f) => uploadImage(f, (u) => onFieldChange(["COLLECTIONS", i, "gallery", j, "image"], u), `${col.id}-g${j}`)}
                            uploading={uploadingKey === `${col.id}-g${j}`} />
                          <div className="grid sm:grid-cols-2 gap-x-5">
                            {["sv", "en"].map((l) => (
                              <div key={l}>
                                <p className="text-[10px] uppercase tracking-[0.14em] text-ink/40 mb-2">{l === "sv" ? "Svenska" : "English"}</p>
                                {renderField(g[l], ["COLLECTIONS", i, "gallery", j, l], "")}
                              </div>
                            ))}
                          </div>
                          <button onClick={() => removeGalleryItem(i, j)} title="Ta bort bild"
                            className="p-2 border border-border rounded hover:border-red-400 text-ink/50 hover:text-red-500 transition-colors">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      ))}
                      {(col.gallery || []).length === 0 && (
                        <p className="text-ink/40 text-sm">Inga bilder i galleriet än — klicka "Lägg till bild".</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "images" && (
          <div className="space-y-12">
            {/* Section images used across the site */}
            <div>
              <h3 className="font-serif text-2xl text-ink mb-1">Sektionsbilder</h3>
              <p className="text-ink/50 text-sm mb-5">Bilderna som används i de stora sektionerna på startsidan.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {SECTION_IMAGE_KEYS.filter((k) => k in content.IMAGES).map((key) => (
                  <ImageField key={key} label={IMAGE_LABELS[key] || key} url={content.IMAGES[key]}
                    onChange={(u) => onFieldChange(["IMAGES", key], u)}
                    onUpload={(f) => uploadImage(f, (u) => onFieldChange(["IMAGES", key], u), key)}
                    uploading={uploadingKey === key} />
                ))}
              </div>
            </div>

            {/* Business area (collection) main images */}
            <div>
              <h3 className="font-serif text-2xl text-ink mb-1">Affärsområden</h3>
              <p className="text-ink/50 text-sm mb-5">Huvudbilden för varje produktområde (visas på kort och områdessida).</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {content.COLLECTIONS.map((col, i) => (
                  <ImageField key={col.id} label={`${col.number} · ${col.sv?.name || col.id}`} url={col.image}
                    onChange={(u) => onFieldChange(["COLLECTIONS", i, "image"], u)}
                    onUpload={(f) => uploadImage(f, (u) => onFieldChange(["COLLECTIONS", i, "image"], u), `area-${col.id}`)}
                    uploading={uploadingKey === `area-${col.id}`} />
                ))}
              </div>
            </div>

            {/* Subcategory (gallery) images */}
            <div>
              <h3 className="font-serif text-2xl text-ink mb-1">Underkategorier (galleri)</h3>
              <p className="text-ink/50 text-sm mb-5">Galleribilderna inne på varje områdessida. Lägg till/ta bort görs i fliken Produkter.</p>
              <div className="space-y-8">
                {content.COLLECTIONS.map((col, i) => (
                  (col.gallery && col.gallery.length > 0) ? (
                    <div key={col.id}>
                      <p className="text-xs uppercase tracking-[0.14em] text-gold mb-3">{col.number} · {col.sv?.name || col.id}</p>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {col.gallery.map((g, j) => (
                          <ImageField key={j} label={g.sv?.title || `Bild ${j + 1}`} url={g.image}
                            onChange={(u) => onFieldChange(["COLLECTIONS", i, "gallery", j, "image"], u)}
                            onUpload={(f) => uploadImage(f, (u) => onFieldChange(["COLLECTIONS", i, "gallery", j, "image"], u), `gal-${col.id}-${j}`)}
                            uploading={uploadingKey === `gal-${col.id}-${j}`} />
                        ))}
                      </div>
                    </div>
                  ) : null
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "partners" && (
          <div className="max-w-2xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-serif text-2xl text-ink">Kunder</h3>
                <p className="text-ink/50 text-sm mt-1">Namnen som rullar i "Anlitade av"-sektionen på startsidan.</p>
              </div>
              <button onClick={addPartner}
                className="flex items-center gap-1.5 text-xs border border-border hover:border-gold text-ink/70 rounded px-3 py-2 transition-colors">
                <Plus size={14} /> Lägg till kund
              </button>
            </div>
            <div className="space-y-2">
              {(content.PARTNERS || []).map((p, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-ink/30 text-xs w-6 text-right">{idx + 1}</span>
                  <input value={p} onChange={(e) => updatePartner(idx, e.target.value)}
                    className="flex-1 border border-border focus:border-gold outline-none rounded px-3 py-2 text-ink text-sm bg-white" />
                  <button onClick={() => removePartner(idx)} title="Ta bort"
                    className="p-2 border border-border rounded hover:border-red-400 text-ink/50 hover:text-red-500 transition-colors">
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
              {(content.PARTNERS || []).length === 0 && (
                <p className="text-ink/40 text-sm">Inga kunder än — klicka "Lägg till kund".</p>
              )}
            </div>
            <p className="text-ink/40 text-xs mt-6">Kom ihåg att klicka "Spara" uppe till höger.</p>
          </div>
        )}


        {tab === "policies" && (
          <div>
            <div className="flex items-center gap-2 mb-8">
              {["sv", "en"].map((l) => (
                <button key={l} onClick={() => setEditLang(l)}
                  className={"px-4 py-1.5 text-xs uppercase tracking-[0.15em] rounded border transition-colors " + (editLang === l ? "bg-ink text-white border-ink" : "border-border text-ink/60 hover:border-gold")}>
                  {l === "sv" ? "Svenska" : "English"}
                </button>
              ))}
              <p className="text-ink/40 text-xs ml-3">Redigerar {editLang === "sv" ? "svenska" : "engelska"} policytexter</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {["quality", "environment"].map((type) => {
                const pol = content.CONTENT[editLang]?.policies?.[type];
                if (!pol) return null;
                return (
                  <div key={type} className="bg-white/60 rounded p-5 border border-border">
                    <h3 className="font-serif text-xl text-ink mb-1">{type === "quality" ? "Kvalitetspolicy" : "Miljöpolicy"}</h3>
                    <p className="text-ink/40 text-xs mb-4">Sida: /policy/{type}</p>
                    {renderField(pol, ["CONTENT", editLang, "policies", type], "")}
                    <div className="mt-2 border-t border-border pt-4">
                      <p className="text-xs uppercase tracking-[0.12em] text-ink/50 mb-2">PDF ({editLang === "sv" ? "svenska" : "engelska"})</p>
                      {pol.pdfUrl ? (
                        <a href={pol.pdfUrl} target="_blank" rel="noreferrer" className="text-gold text-sm hover:underline block mb-2" data-testid={`admin-policy-pdf-link-${type}`}>Visa nuvarande PDF ↗</a>
                      ) : (
                        <p className="text-ink/40 text-xs mb-2">Ingen PDF uppladdad än.</p>
                      )}
                      <label className="flex items-center justify-center gap-2 cursor-pointer bg-ink hover:bg-ink-soft text-white text-xs py-2 rounded transition-colors">
                        {uploadingKey === `pdf-${type}-${editLang}` ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                        Ladda upp PDF
                        <input type="file" accept=".pdf,application/pdf" className="hidden"
                          onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0], (u) => onFieldChange(["CONTENT", editLang, "policies", type, "pdfUrl"], u), `pdf-${type}-${editLang}`)} />
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-ink/40 text-xs mt-6">Kom ihåg att klicka "Spara" uppe till höger.</p>
          </div>
        )}

        {tab === "enquiries" && (
          <div className="space-y-3">
            {enquiries.length === 0 && <p className="text-ink/50">Inga förfrågningar än.</p>}
            {enquiries.map((e) => (
              <div key={e.id} className={"border rounded p-5 bg-white/70 " + (e.read ? "border-border" : "border-gold")}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-serif text-lg text-ink">{e.name} <span className="text-ink/40 text-sm font-sans">· {e.company}</span></p>
                    <a href={`mailto:${e.email}`} className="text-gold text-sm hover:underline">{e.email}</a>
                    <p className="mt-3 text-ink/75 text-sm whitespace-pre-wrap">{e.message}</p>
                    <p className="mt-3 text-ink/40 text-xs">{new Date(e.created_at).toLocaleString("sv-SE")}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => markRead(e.id, !e.read)} title={e.read ? "Markera oläst" : "Markera läst"}
                      className="p-2 border border-border rounded hover:border-gold text-ink/60 transition-colors">
                      {e.read ? <MailOpen size={16} /> : <Mail size={16} />}
                    </button>
                    <button onClick={() => delEnquiry(e.id)} className="p-2 border border-border rounded hover:border-red-400 text-ink/60 hover:text-red-500 transition-colors">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Admin;
